// src/album/SpecialPages.jsx
import React from 'react';
import styled from 'styled-components';

// --- Icon Imports ---
const IconPlane = () => <span style={{ fontSize: '1.4em' }}>✈️</span>;
const IconCalendar = () => <span style={{ fontSize: '1.4em' }}>📅</span>;
const IconCity = () => <span style={{ fontSize: '1.4em' }}>🏙️</span>;
const IconCamera = () => <span style={{ fontSize: '1.4em' }}>📸</span>;
const IconGlobe = () => <span style={{ fontSize: '2em' }}>🌍</span>;
const IconBook = () => <span style={{ fontSize: '1.4em' }}>📖</span>;
const IconHome = () => <span style={{ fontSize: '2em' }}>🏠</span>;
const IconPin = () => <span style={{ fontSize: '2em' }}>📍</span>;

// --- Country Code to Flag Emoji Converter ---
const getCountryFlag = (countryCode) => {
  if (!countryCode || countryCode.length !== 2) return '🏳️';
  
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt());
  
  return String.fromCodePoint(...codePoints);
};

// --- Convert country name to ISO 2-letter code ---
const countryNameToCode = (countryName) => {
  if (!countryName) return null;
  
  // Normalize: lowercase, trim
  const normalized = countryName.toLowerCase().trim();
  
  // Comprehensive mapping of country names to ISO 2-letter codes
  const countryMap = {
    // Popular destinations
    'united states': 'US',
    'usa': 'US',
    'united states of america': 'US',
    'united kingdom': 'GB',
    'uk': 'GB',
    'great britain': 'GB',
    'england': 'GB',
    'scotland': 'GB',
    'wales': 'GB',
    'northern ireland': 'GB',
    
    // Europe
    'croatia': 'HR',
    'italy': 'IT',
    'france': 'FR',
    'spain': 'ES',
    'germany': 'DE',
    'netherlands': 'NL',
    'belgium': 'BE',
    'switzerland': 'CH',
    'austria': 'AT',
    'portugal': 'PT',
    'greece': 'GR',
    'poland': 'PL',
    'czech republic': 'CZ',
    'czechia': 'CZ',
    'hungary': 'HU',
    'romania': 'RO',
    'ireland': 'IE',
    'denmark': 'DK',
    'sweden': 'SE',
    'norway': 'NO',
    'finland': 'FI',
    'iceland': 'IS',
    'luxembourg': 'LU',
    'monaco': 'MC',
    'vatican city': 'VA',
    'san marino': 'SM',
    'malta': 'MT',
    'cyprus': 'CY',
    'slovenia': 'SI',
    'slovakia': 'SK',
    'estonia': 'EE',
    'latvia': 'LV',
    'lithuania': 'LT',
    'bulgaria': 'BG',
    'serbia': 'RS',
    'montenegro': 'ME',
    'bosnia and herzegovina': 'BA',
    'albania': 'AL',
    'north macedonia': 'MK',
    'macedonia': 'MK',
    'kosovo': 'XK',
    
    // Asia
    'japan': 'JP',
    'china': 'CN',
    'south korea': 'KR',
    'korea': 'KR',
    'thailand': 'TH',
    'vietnam': 'VN',
    'singapore': 'SG',
    'malaysia': 'MY',
    'indonesia': 'ID',
    'philippines': 'PH',
    'india': 'IN',
    'nepal': 'NP',
    'sri lanka': 'LK',
    'maldives': 'MV',
    'cambodia': 'KH',
    'laos': 'LA',
    'myanmar': 'MM',
    'brunei': 'BN',
    'taiwan': 'TW',
    'hong kong': 'HK',
    'macau': 'MO',
    
    // Middle East
    'israel': 'IL',
    'jordan': 'JO',
    'turkey': 'TR',
    'united arab emirates': 'AE',
    'uae': 'AE',
    'dubai': 'AE',
    'saudi arabia': 'SA',
    'qatar': 'QA',
    'kuwait': 'KW',
    'bahrain': 'BH',
    'oman': 'OM',
    'lebanon': 'LB',
    'egypt': 'EG',
    
    // Americas
    'canada': 'CA',
    'mexico': 'MX',
    'brazil': 'BR',
    'argentina': 'AR',
    'chile': 'CL',
    'peru': 'PE',
    'colombia': 'CO',
    'ecuador': 'EC',
    'bolivia': 'BO',
    'uruguay': 'UY',
    'paraguay': 'PY',
    'venezuela': 'VE',
    'costa rica': 'CR',
    'panama': 'PA',
    'guatemala': 'GT',
    'cuba': 'CU',
    'jamaica': 'JM',
    'bahamas': 'BS',
    'barbados': 'BB',
    'trinidad and tobago': 'TT',
    
    // Oceania
    'australia': 'AU',
    'new zealand': 'NZ',
    'fiji': 'FJ',
    'papua new guinea': 'PG',
    'samoa': 'WS',
    'tonga': 'TO',
    'vanuatu': 'VU',
    
    // Africa
    'south africa': 'ZA',
    'egypt': 'EG',
    'morocco': 'MA',
    'tunisia': 'TN',
    'kenya': 'KE',
    'tanzania': 'TZ',
    'uganda': 'UG',
    'rwanda': 'RW',
    'ethiopia': 'ET',
    'ghana': 'GH',
    'nigeria': 'NG',
    'senegal': 'SN',
    'namibia': 'NA',
    'botswana': 'BW',
    'zimbabwe': 'ZW',
    'zambia': 'ZM',
    'madagascar': 'MG',
    'mauritius': 'MU',
    'seychelles': 'SC'
  };
  
  return countryMap[normalized] || null;
};

// --- Get unique countries from journey steps ---
const extractCountriesFromSteps = (meta) => {
  const uniqueCountryCodes = new Set();
  
  if (meta?.journeySteps) {
    meta.journeySteps.forEach(step => {
      if (step.country) {
        // If it's already a 2-letter code, use it directly
        if (step.country.length === 2) {
          uniqueCountryCodes.add(step.country.toUpperCase());
        } else {
          // Convert country name to code
          const code = countryNameToCode(step.country);
          if (code) {
            uniqueCountryCodes.add(code);
          }
        }
      }
    });
  }
  
  return Array.from(uniqueCountryCodes);
};

// --- Shared Styled Components ---
const PageBackground = styled.div`
  background-color: #f9f7f5;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 30px;
  box-sizing: border-box;
  font-family: 'Lato', sans-serif;
  color: #333;
  position: relative;
  overflow: hidden;
`;

const PageTitle = styled.h2`
  font-family: 'Playfair Display', serif;
  font-size: 2em;
  font-weight: 700;
  color: #222;
  margin-bottom: 25px;
  text-align: center;
  margin-top: 0;
`;

const formatNumber = (num) => (num || 0).toLocaleString();

// --- NEW: Flags Row Component (below all cards) ---
const FlagsRow = ({ countryCodes }) => {
  const displayFlags = countryCodes.slice(0, 10).map(code => getCountryFlag(code)); // Show up to 10 flags
  const hasMore = countryCodes.length > 10;
  
  if (displayFlags.length === 0) return null;
  
  return (
    <FlagsRowContainer>
      {displayFlags.map((flag, idx) => (
        <FlagEmoji key={idx}>{flag}</FlagEmoji>
      ))}
      {hasMore && <MoreText>+{countryCodes.length - 10}</MoreText>}
    </FlagsRowContainer>
  );
};

// --- Journey Arc Component ---
function JourneyArc({ meta }) {
  const journeySteps = meta?.journeySteps || [];
  const hasHome = !!meta?.hasHome;
  const homeLabel = meta?.homeLocation || 'Home';

  const pathPoints = [];

  if (hasHome) {
    pathPoints.push({
      id: 'home',
      label: homeLabel,
      isHome: true,
      photoCount: 0,
      days: 0,
      country: null
    });
  }

  // Build all points with significance metrics
  journeySteps.forEach((step, index) => {
    const cityName = step.city || step.location || `Chapter ${index + 1}`;
    
    pathPoints.push({
      id: step.id || `step-${index}`,
      label: cityName, // City only, no country
      isHome: false,
      isLast: index === journeySteps.length - 1,
      photoCount: step.photoCount || 0,
      days: step.days || 0,
      country: step.country || null,
      originalIndex: index
    });
  });

  // Determine which chapters should be labeled (max 11: first + last + 9 most significant with country diversity)
  const shouldShowLabel = (point, index, allPoints) => {
    // Always show first and last
    if (index === 0 || index === allPoints.length - 1) return true;
    
    // If 11 or fewer total points, show all labels
    if (allPoints.length <= 11) return true;
    
    // Otherwise, select up to 9 middle points with country diversity
    const middlePoints = allPoints.slice(1, -1); // Exclude first and last
    
    // Group points by country
    const byCountry = new Map();
    middlePoints.forEach(p => {
      const country = p.country || 'unknown';
      if (!byCountry.has(country)) byCountry.set(country, []);
      byCountry.get(country).push(p);
    });
    
    // Strategy: Pick the best point from each country first, then fill remaining slots
    const selectedPoints = [];
    const targetCount = 9; // 9 middle points + first + last = 11 total
    
    // First pass: Select best point from each country (ensures diversity)
    for (const [country, points] of byCountry.entries()) {
      if (selectedPoints.length >= targetCount) break;
      
      // Sort by significance (photos weighted 2x + days)
      const sorted = [...points].sort((a, b) => {
        const scoreA = (a.photoCount * 2) + a.days;
        const scoreB = (b.photoCount * 2) + b.days;
        return scoreB - scoreA;
      });
      
      // Take the best one from this country
      if (sorted.length > 0) {
        selectedPoints.push(sorted[0]);
      }
    }
    
    // Second pass: If we still have slots, fill with next best overall
    if (selectedPoints.length < targetCount) {
      const remaining = middlePoints
        .filter(p => !selectedPoints.includes(p))
        .sort((a, b) => {
          const scoreA = (a.photoCount * 2) + a.days;
          const scoreB = (b.photoCount * 2) + b.days;
          return scoreB - scoreA;
        });
      
      const slotsNeeded = targetCount - selectedPoints.length;
      selectedPoints.push(...remaining.slice(0, slotsNeeded));
    }
    
    return selectedPoints.includes(point);
  };

   const arcWidth = 800;
  const arcHeight = 220;
  const centerX = arcWidth / 2;
  const startX = 60;      // CHANGED: Was 10, now 60 for label space
  const endX = arcWidth - 60;  // CHANGED: Was arcWidth - 10, now -60

  // Get all labeled point indices for positioning logic
  const labeledIndices = pathPoints
    .map((p, i) => ({ point: p, index: i }))
    .filter(({ point: p, index: i }) => shouldShowLabel(p, i, pathPoints))
    .map(({ index: i }) => i);

  const positions = pathPoints.map((point, index) => {
    const progress = pathPoints.length === 1 ? 0.5 : index / (pathPoints.length - 1);
    const x = startX + (endX - startX) * progress;
    
    // Create DOWNWARD arc curve (smiley face)
    const normalizedX = (x - centerX) / (arcWidth / 2);
    const arcY = arcHeight * 0.1 + (arcHeight * 0.9 * (1 - normalizedX * normalizedX));
    
    // Determine if this point should have a label
    const showLabel = shouldShowLabel(point, index, pathPoints);
    
    // Better alternating algorithm for label positioning (optimized for up to 11 labels)
    let textY;
    
    if (index === 0 || index === pathPoints.length - 1) {
      // First and last ALWAYS above
      textY = arcY - 25;
    } else if (!showLabel) {
      // No label for this point
      textY = null;
    } else {
      // Smart positioning for labeled middle points
      const positionInLabeled = labeledIndices.indexOf(index);
      const totalLabeled = labeledIndices.length;
      
      // For middle point (the bottom of the arc), put label below
      const isNearCenter = Math.abs(x - centerX) < 80;
      
      if (isNearCenter) {
        // Center point - label goes below (at the bottom of the arc)
        textY = arcY + 35;
      } else if (x < centerX) {
        // Left side of arc
        // Alternate: odd positions below, even positions above
        textY = (positionInLabeled % 2 === 0) ? arcY + 30 : arcY - 25;
      } else {
        // Right side of arc
        // Alternate: odd positions above, even positions below (inverted from left)
        textY = (positionInLabeled % 2 === 0) ? arcY - 25 : arcY + 30;
      }
      
      // Edge safety: if too close to edges, prefer above
      if ((x < 100 || x > arcWidth - 100) && textY > arcY) {
        textY = arcY - 25;
      }
    }
    
    return { 
      ...point, 
      x, 
      y: arcY, 
      textY,
      showLabel,
      dotSize: showLabel ? 6 : 4 // Larger dots for labeled points
    };
  });

  const pathData = positions.reduce((path, pos, index) => {
    if (index === 0) {
      return `M ${pos.x} ${pos.y}`;
    }
    const prevPos = positions[index - 1];
    const midX = (prevPos.x + pos.x) / 2;
    const midY = Math.max(prevPos.y, pos.y) + 15; // Changed from min to max, and - to + for downward curve
    return `${path} Q ${midX} ${midY} ${pos.x} ${pos.y}`;
  }, '');

  return (
    <JourneyContainer>
      <svg width={arcWidth} height={arcHeight + 100} style={{ overflow: 'visible' }}>
        {positions.length > 1 && (
          <path
            d={pathData}
            stroke="#4A90E2"
            strokeWidth="2"
            fill="none"
            strokeDasharray="4,2"
            opacity="0.8"
          />
        )}
        
        {positions.map((pos, index) => (
          <g key={pos.id}>
            {/* Point circle - use dynamic dotSize and opacity */}
            <circle
              cx={pos.x}
              cy={pos.y}
              r={pos.dotSize || 4}
              fill="#10b981"
              stroke="#fff"
              strokeWidth="2"
              opacity={pos.showLabel ? 1 : 0.6}
            />
            
            {/* Label - only show if showLabel is true */}
            {pos.showLabel && pos.textY && (
              <text
                x={pos.x}
                y={pos.textY}
                textAnchor={(() => {
                  // FIXED: Special handling for first and last points at edges
                  const isFirst = index === 0;
                  const isLast = index === positions.length - 1;
                  const isBelow = pos.textY > pos.y;
                  const isLeftOfCenter = pos.x < centerX;
                  
                  // First point: text starts from dot, extends right
                  if (isFirst) {
                    return "start";
                  }
                  
                  // Last point: text ends at dot, extends left  
                  if (isLast) {
                    return "end";
                  }
                  
                  // Middle points above arc: center the text
                  if (!isBelow) {
                    return "middle";
                  }
                  
                  // Below arc - align based on side
                  if (isLeftOfCenter) {
                    return "end";
                  } else {
                    return "start";
                  }
                })()}
                fontSize="12"
                fill="#555"
                fontWeight="600"
              >
                {pos.label.length > 20 ? `${pos.label.substring(0, 20)}...` : pos.label}
              </text>
            )}
            
            {/* Home indicator for first point */}
            {index === 0 && pos.isHome && (
              <text
                x={pos.x}
                y={pos.y - 12}
                textAnchor="middle"
                fontSize="12"
                fill="#10b981"
              >
                🏠
              </text>
            )}
          </g>
        ))}
      </svg>
    </JourneyContainer>
  );
}

// --- ENHANCED StatsCard with Countries Circle ---
export const StatsCard = ({ meta }) => {
  const countryCodes = extractCountriesFromSteps(meta);
  
  return (
    <PageBackground style={{ paddingTop: '5%', justifyContent: 'flex-start' }}>
      {/* Single Title */}
      <PageTitle style={{ marginBottom: '4%', fontSize: '2em' }}>
        YOUR JOURNEY IN NUMBERS
      </PageTitle>

      {/* Layout: 2 cards | Circle | 2 cards */}
      <StatsWithCircleGrid>
        {/* Left Column: 2 Cards */}
        <LeftCardsColumn>
          <CompactMetricCard>
            <IconPlane />
            <strong>{formatNumber(meta?.totalMiles)}</strong>
            <span>MILES</span>
          </CompactMetricCard>
          <CompactMetricCard>
            <IconCalendar />
            <strong>{formatNumber(meta?.days)}</strong>
            <span>DAYS</span>
          </CompactMetricCard>
        </LeftCardsColumn>

        {/* Center: Countries Circle (without flags) */}
        <CenterCircleColumn>
          <CircleWrapper>
            <span style={{ fontSize: '1.4em' }}>🌍</span>
            <CountryNumber>{meta?.countries || 0}</CountryNumber>
            <CountryLabel>COUNTRIES</CountryLabel>
          </CircleWrapper>
        </CenterCircleColumn>

        {/* Right Column: 2 Cards */}
        <RightCardsColumn>
          <CompactMetricCard>
            <IconBook />
            <strong>{formatNumber(meta?.stepsLogged)}</strong>
            <span>CHAPTERS</span>
          </CompactMetricCard>
          <CompactMetricCard>
            <IconCamera />
            <strong>{formatNumber(meta?.photos)}</strong>
            <span>MOMENTS</span>
          </CompactMetricCard>
        </RightCardsColumn>
      </StatsWithCircleGrid>

      {/* Flags below all cards */}
      <FlagsRow countryCodes={countryCodes} />

      {/* Journey Arc */}
      <JourneyArcContainer>
        <JourneyArc meta={meta} />
      </JourneyArcContainer>
    </PageBackground>
  );
};

// --- Styled Components ---

// New Layout: 2 Cards | Circle | 2 Cards
const StatsWithCircleGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 20px;
  width: 90%;
  margin: 0 0 20px 0;
  align-items: center;
`;

const LeftCardsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  justify-content: center;
`;

const CenterCircleColumn = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const RightCardsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  justify-content: center;
`;

// Countries Circle Styles
const CircleWrapper = styled.div`
  width: 140px;
  height: 140px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(190, 227, 248, 0.95) 0%, rgba(198, 246, 213, 0.95) 100%);
  border: 3px solid rgba(66, 153, 225, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  position: relative;
  
  /* Remove animation for better PDF rendering */
  @media print {
    animation: none !important;
    opacity: 1 !important;
  }
`;

const CountryNumber = styled.div`
  font-family: 'Playfair Display', serif;
  font-size: 2.2em;
  font-weight: 700;
  color: #1a202c;
  line-height: 1;
  margin: 5px 0 2px 0;
  
  @media print {
    color: #000000 !important;
    opacity: 1 !important;
  }
`;

const CountryLabel = styled.div`
  font-size: 0.65em;
  color: #4a5568;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
  
  @media print {
    color: #2d3748 !important;
    opacity: 1 !important;
  }
`;

// Flags Row Styles (below all cards)
const FlagsRowContainer = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  max-width: 500px;
  margin: 0 0 20px 0;
`;

const FlagEmoji = styled.span`
  font-size: 1.8em;
  line-height: 1;
`;

const MoreText = styled.span`
  font-size: 0.8em;
  color: #718096;
  font-weight: 600;
`;

// Stat Cards Styles - BIGGER CARDS
const CompactMetricCard = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  background-color: rgba(255, 255, 255, 0.95);
  padding: 18px 18px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.03);
  min-width: 200px;
  min-height: 70px;

  @media print {
    background-color: #ffffff !important;
    opacity: 1 !important;
  }

  span:first-child {
    font-size: 1.7em;
    flex-shrink: 0;
    width: 35px;
  }

  strong {
    font-family: 'Playfair Display', serif;
    font-size: 2em;
    font-weight: 700;
    color: #4A90E2;
    line-height: 1;
    flex-shrink: 0;
    text-align: center;
    flex: 0 0 auto;
  }

  span:last-child {
    font-size: 0.75em;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    white-space: nowrap;
    text-align: right;
    width: 75px;
  }
`;

const JourneyArcContainer = styled.div`
  width: 100%;
  margin: 10px 0 25px 0;
  display: flex;
  justify-content: center;
  padding: 0 5%;
`;

const JourneyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 540px;
`;

// --- MapCard (Keep original) ---
export const MapCard = ({ meta }) => {
  const { svgDataUrl, points, countries } = meta || {};
  
  console.log('MapCard received meta:', { svgDataUrl: !!svgDataUrl, points: points?.length, countries });
  
  return (
    <PageBackground style={{ backgroundColor: '#0f172a', color: '#fff', padding: 0 }}>
      {!meta?.hideTitle && (
        <PageTitle style={{ color: '#94a3b8', margin: '16px 0 0 16px', position: 'absolute', zIndex: 1 }}>
          YOUR ROUTE
        </PageTitle>
      )}

      <div style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden'
      }}>
        {svgDataUrl ? (
          <img
            src={svgDataUrl}
            alt="Route map"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
            onError={(e) => { console.error('Map image failed to load:', e); e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <div style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#94a3b8',
            fontSize: 16
          }}>
            {points && points.length > 0 ? 'Generating route map...' : 'No GPS data available'}
          </div>
        )}
      </div>

      {points && points.length > 0 && (
        <div style={{
          position: 'absolute',
          bottom: 16,
          left: 0,
          right: 0,
          textAlign: 'center',
          color: 'rgba(255,255,255,0.9)',
          fontSize: 14,
          textShadow: '0 1px 2px rgba(0,0,0,0.5)'
        }}>
          {points.length} locations • {meta?.countries || 0} countries
        </div>
      )}
    </PageBackground>
  );
};

// --- KEEP ORIGINAL STATS CALCULATION LOGIC ---
export const buildStatsFromSteps = (steps) => {
  const stats = {
    photos: 0, 
    days: 0, 
    cities: 0, 
    countries: 0, 
    totalMiles: 0,
    furthestPointDistance: 0, 
    homeLocation: "Home", 
    furthestLocation: "Furthest Point",
    stepsLogged: steps?.length || 0,
    journeySteps: []
  };
  
  if (!steps || !steps.length) return stats;

  const dates = new Set();
  const uniqueCities = new Set();
  const uniqueCountries = new Set();
  const coordsList = [];
  let homeCoords = null;
  let homeLabel = 'Home';

  const journeySteps = [];
  let lastCity = null;

  steps.forEach((step, index) => {
    stats.photos += Array.isArray(step.photos) ? step.photos.length : 0;
    // Collect ALL dates including endDate for merged steps
    if (step.date) dates.add(new Date(step.date).toDateString());
    if (step.startDate) dates.add(new Date(step.startDate).toDateString());
    if (step.endDate) dates.add(new Date(step.endDate).toDateString());

    const addr = step.address || {};
    const city = addr.city || addr.town || addr.village || step.location;
    const country = addr.country || addr.country_code;
    
    if (city) uniqueCities.add(String(city).trim());
    if (country) uniqueCountries.add(String(country).trim());

    if (city || country) {
      const normalizedCity = String(city || '').trim().toLowerCase();
      
      // If this is the same city as the last step, merge them
      if (lastCity && normalizedCity && lastCity.normalizedName === normalizedCity) {
        // Add photos to the last journey step
        lastCity.photoCount += Array.isArray(step.photos) ? step.photos.length : 0;
        lastCity.days += 1;
      } else {
        // New city - create a new journey step
        const newStep = {
          id: `step-${index}`,
          city: city,
          country: country,
          location: step.location || city || country,
          photoCount: Array.isArray(step.photos) ? step.photos.length : 0,
          days: 1,
          normalizedName: normalizedCity // Track for comparison
        };
        journeySteps.push(newStep);
        lastCity = newStep;
      }
    }

    const coords = extractCoordinatesFromStep(step);
    if (coords) {
      if (!homeCoords) {
        homeCoords = coords;
        homeLabel = city || country || "Starting Point";
      }
      coordsList.push(coords);
    }
  });

  if (homeCoords && coordsList.length > 0) {
    let maxDistance = 0;
    let furthestLabel = homeLabel;
    coordsList.forEach(coords => {
      const dist = haversineDistance(homeCoords, coords);
      if (dist > maxDistance) {
        maxDistance = dist;
        const step = steps.find(s => extractCoordinatesFromStep(s) === coords);
        furthestLabel = step?.address?.city || step?.location || "Furthest Point";
      }
    });
    stats.furthestPointDistance = Math.round(maxDistance);
    stats.furthestLocation = furthestLabel;
  }
  
  for (let i = 1; i < coordsList.length; i++) {
    stats.totalMiles += haversineDistance(coordsList[i - 1], coordsList[i]);
  }

  // Calculate days as date RANGE (last date - first date + 1), not count of unique dates
  // This gives accurate trip duration even when some days have no steps
  if (dates.size > 0) {
    const dateArray = Array.from(dates).map(d => new Date(d));
    const minDate = new Date(Math.min(...dateArray));
    const maxDate = new Date(Math.max(...dateArray));
    stats.days = Math.max(1, Math.round((maxDate - minDate) / 86400000) + 1);
  } else {
    stats.days = 0;
  }
  
  stats.cities = uniqueCities.size;
  stats.countries = uniqueCountries.size;
  stats.homeLocation = homeLabel;
  stats.totalMiles = Math.round(stats.totalMiles);
  stats.journeySteps = journeySteps;

  return stats;
};

function extractCoordinatesFromStep(step) {
  if (!step) return null;
  const addr = step.address || {};
  const lat = step.lat ?? addr.lat ?? step.location?.lat;
  const lng = step.lng ?? addr.lng ?? step.location?.lng;
  if (lat != null && lng != null) return { lat: +lat, lng: +lng };
  
  const photoWithCoords = (step.photos || []).find(p => p.coordinates && Number.isFinite(p.coordinates.lat));
  return photoWithCoords ? photoWithCoords.coordinates : null;
}

function haversineDistance(coords1, coords2) {
  const toRad = (x) => x * Math.PI / 180;
  const R = 3958.8; // Miles
  const dLat = toRad(coords2.lat - coords1.lat);
  const dLon = toRad(coords2.lng - coords1.lng);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(coords1.lat)) * Math.cos(toRad(coords2.lat)) * Math.sin(dLon / 2) ** 2;
  return R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
}

export const pointsFromSteps = (steps) => {
  return [];
};

export function TimelineCard({ page }) {
  const days = page?.meta?.days || [];
  return (
    <div style={{
      width: '100%', height: '100%',
      padding: '28px 36px',
      fontFamily: 'ui-sans-serif, system-ui',
      color: '#000000'
    }}>
      <div style={{ fontSize: 18, fontWeight: 600, letterSpacing: '.02em', marginBottom: 14, opacity: .8 }}>
        Trip Timeline
      </div>

      <div style={{ display: 'grid', gap: 10 }}>
        {days.map((d, i) => (
          <div key={i} style={{
            display: 'grid',
            gridTemplateColumns: '120px 1fr',
            gap: 12,
            alignItems: 'baseline',
            padding: '8px 0',
            borderBottom: '1px dashed rgba(0,0,0,.08)'
          }}>
            <div style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: 'black' }}>
              {d.when}
            </div>
            <div style={{ color: 'black' }}>{(d.places || []).join(' • ')}</div>
          </div>
        ))}
      </div>
    </div>
  );
}